/**
 * The queue service's clock, as an offset from this device's. A held slot expires by the service's clock
 * (`hold_expires_at`), so the countdown must not follow a device clock that is minutes off. The offset is
 * taken from the `Date` header of the service's own answers (same origin through the site's proxy, so the
 * header is readable); until one arrives the device clock is used as it is.
 */
let offsetMs = 0;

/** A round trip longer than this says too little about when the header was written. */
const MAX_ROUND_TRIP_MS = 5_000;
/** Below this the change is the header's one-second rounding, not a clock that is off: keep the countdown steady. */
const MIN_CHANGE_MS = 1_000;

/**
 * Server time minus device time (ms) from one answer's `Date` header, or null when it cannot be told. The
 * header is whole seconds, so its middle (+500 ms) is set against the middle of the round trip.
 */
export const offsetFromDateHeader = (header: string | null | undefined, sentAt: number, receivedAt: number): number | null => {
  if (!header || receivedAt < sentAt || receivedAt - sentAt > MAX_ROUND_TRIP_MS) return null;
  const server = Date.parse(header);
  if (!Number.isFinite(server)) return null;
  return Math.round(server + 500 - (sentAt + receivedAt) / 2);
};

/** Takes the service's clock from one answer (ignores noise below a second). */
export const noteServerDate = (header: string | null | undefined, sentAt: number, receivedAt = Date.now()) => {
  const next = offsetFromDateHeader(header, sentAt, receivedAt);
  if (next === null || Math.abs(next - offsetMs) < MIN_CHANGE_MS) return;
  offsetMs = next;
};

/** «Now» by the service's clock (ms). */
export const serverNow = () => Date.now() + offsetMs;

/** Tests only. */
export const resetServerClock = () => {
  offsetMs = 0;
};
